import { useLoaderData, useParams } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import BrowserTimezoneAlert from "components/BrowserTimezoneAlert";
import EmptyStatePlaceholder from "components/EmptyStatePlaceholder";
import FilterSection from "components/FilterSection";
import LoadingStateProgressBar from "components/LoadingStateProgressBar";
import PageHeader from "components/PageHeader";
import SearchParamControlledAutocomplete from "components/SearchParamControlledAutocomplete";
import StickyTableCell from "components/StickyTableCell";
import SyncRunStatus from "components/SyncRunStatus";
import TableContainerWithScrollShadow from "components/TableContainerWithScrollShadow";

import { getCronDescriptionWithTzOffset } from "helpers/cron";
import { capitalizeFirstChar } from "helpers/string";
import { formatTimestamp } from "helpers/datetime";
import { getLoggedInUser } from "helpers/auth";
import buildRoute from "../components/buildRoute";

function getUniqueValuesOfKeyFromObjectList(list, key) {
  return [...new Set(list.map((item) => item[key]).filter(Boolean))];
}

function ManagedSyncsIndexPage() {
  const { projectId } = useParams();
  const { syncs, allVendorNames, isSuperUser } = useLoaderData();

  // Some custom styling for header cells
  const firstHeaderCellStyles = {
    minWidth: 200,
    zIndex: 10,
    borderBottom: "none",
  };
  const headerCellStyles = {
    ...firstHeaderCellStyles,
    zIndex: 1,
    border: "none",
  };

  return (
    <>
      <PageHeader
        title="Managed Syncs"
        subtitle="Syncs managed and created by CTA for this project"
      />
      <Box
        component="section"
        sx={{
          pt: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <BrowserTimezoneAlert sx={{ mb: 2 }} />

        <FilterSection sx={{ mb: 1, zIndex: 2 }}>
          <SearchParamControlledAutocomplete
            searchParam="vendorName"
            label="Source"
            options={allVendorNames ?? []}
          />
        </FilterSection>

        <LoadingStateProgressBar />

        {syncs.length ? (
          <TableContainerWithScrollShadow sx={{ flex: 1 }}>
            <Table sx={{ minWidth: 650 }} stickyHeader aria-label="Syncs table">
              <TableHead>
                <TableRow>
                  <StickyTableCell sx={firstHeaderCellStyles}>
                    Sync Name
                  </StickyTableCell>
                  {isSuperUser && <TableCell sx={headerCellStyles}>Source</TableCell>}
                  <TableCell sx={headerCellStyles}>Schedule</TableCell>
                  <TableCell sx={headerCellStyles}>Last run status</TableCell>
                  <TableCell sx={headerCellStyles}>Last run end</TableCell>
                  <TableCell sx={headerCellStyles}>Next run</TableCell>
                  {isSuperUser && <TableCell sx={headerCellStyles}>Sync Type</TableCell>}
                  {isSuperUser && <TableCell sx={headerCellStyles}>Sync ID</TableCell>}
                  {isSuperUser && <TableCell sx={headerCellStyles}>Tags</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {syncs.map((sync) => {
                  const tags = sync.additional_info?.dag?.tags;
                  return (
                    <TableRow key={sync.sync_id}>
                      <StickyTableCell>
                        {sync.sync_name ?? "-"}
                      </StickyTableCell>
                      {isSuperUser && <TableCell>{sync.vendor_name ?? "-"}</TableCell>}
                      <TableCell>
                        {sync.schedule
                          ? getCronDescriptionWithTzOffset(sync.schedule)
                          : "No schedule provided"}
                      </TableCell>
                      <TableCell>
                        <SyncRunStatus status={sync.latest_state} />
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(sync.latest_execution_end_time) ??
                          "-"}
                      </TableCell>
                      <TableCell>
                        {formatTimestamp(sync.additional_info?.dag?.next_dagrun_data_interval_end) ??
                          "-"}
                      </TableCell>
                      {isSuperUser && <TableCell>{sync.sync_type ? capitalizeFirstChar(sync.sync_type) : "-"}</TableCell>}
                      {isSuperUser && <TableCell>{sync.sync_id ?? "-"}</TableCell>}
                      {isSuperUser && <TableCell>
                        {Array.isArray(tags) && tags.length > 0
                          ? tags.map((tagObj) => tagObj.name).join(", ")
                          : "-"}
                      </TableCell>}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainerWithScrollShadow>
        ) : (
          <EmptyStatePlaceholder
            title="No syncs to show"
            subtitle="Try adjusting your filters above. Otherwise, CTA-managed syncs will appear here after they are set up."
          />
        )}
      </Box>
    </>
  );
}

const routeArgs = {
  element: <ManagedSyncsIndexPage />,
  loader: async ({ request, params }) => {
    const { searchParams } = new URL(request.url);

    const loggedInUser = await getLoggedInUser();
    const isSuperUser = loggedInUser?.is_superuser || false;

    const vendorName = searchParams.get("vendorName");
    const projectId = params.projectId;

    const queryParams = new URLSearchParams();
    if (vendorName) queryParams.set("vendor_name", vendorName);
    if (projectId) queryParams.set("project_id", projectId);

    const response = await fetch(`/syncs/api/syncs/?${queryParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const syncs = await response.json();

    const unfilteredParams = new URLSearchParams();
    if (projectId) unfilteredParams.set("project_id", projectId);

    // Perform a second fetch to get the complete list of vendors for the vendor filter dropdown
    const unfilteredResponse = await fetch(
      `/syncs/api/syncs/?${unfilteredParams.toString()}`
    );
    if (!unfilteredResponse.ok) {
      throw new Error(`HTTP error! status: ${unfilteredResponse.status}`);
    }
    const unfilteredSyncs = await unfilteredResponse.json();

    const allVendorNames = getUniqueValuesOfKeyFromObjectList(
      unfilteredSyncs,
      "vendor_name"
    ).sort();

    return { syncs, allVendorNames, isSuperUser };
  },
};

export default buildRoute(routeArgs);
